import PropTypes from 'prop-types'
import React, { useRef, useState, useContext } from 'react'

import moment from 'moment'

import WebView from 'react-native-webview'

import {
  View,
  Platform,
  StatusBar,
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
  KeyboardAvoidingView,
  Dimensions
} from 'react-native'

import {
  Colors,
  useTheme
} from '@vfgroup-oneplatform/foundation/Components/Themes'

import images from '@vfgroup-oneplatform/foundation/Components/Themes/Images'
import {
  Icon,
  VFText,
  VFButton,
  VFImageHeader,
  QuickActionMenu,
  HorizontalStepper
} from '@vfgroup-oneplatform/foundation/Components'

import LinearGradient from 'react-native-linear-gradient'

import IdStep from '../IdStep'
import Loading from '../Loading'
import OfferStep from '../OfferStep'
import GroupStep from '../GroupStep'
import SummaryStep from '../SummaryStep'
import InstallationStep from '../InstallationStep'
import VFTextWithLoader from '../VFTextWithLoader'

import UnlimitedContext from '../../Context'

import { BUNDLE_TYPE, getSelectedBundleData } from '../../Utils/bundle'

import UhImages from '../../Themes/Images'
import styles from './UnlimitedHomeJourney.Styles'
import ReactNativeModal from 'react-native-modal'

const STEPS = [
  { i18nKey: 'unlimited_offer_step', accessibilityLabel: '' },
  { i18nKey: 'unlimited_id_step', accessibilityLabel: '' },
  { i18nKey: 'unlimited_group_step', accessibilityLabel: '' },
  { i18nKey: 'unlimited_installation_step', accessibilityLabel: '' },
  { i18nKey: 'unlimited_summary_step', accessibilityLabel: '' }
]

const NO_INSTALLATION_STEPS = [
  { i18nKey: 'unlimited_offer_step', accessibilityLabel: '' },
  { i18nKey: 'unlimited_group_step', accessibilityLabel: '' },
  { i18nKey: 'unlimited_installation_step', accessibilityLabel: '' },
  { i18nKey: 'unlimited_summary_step', accessibilityLabel: '' }
]

const UnlimitedHomeJourney = props => {
  const scrollViewRef = useRef(null)
  const {
    data: { bundleType, hasInstallation, customBundleOptions: CBO }
  } = useContext(UnlimitedContext)

  const isCustomBundle = bundleType === BUNDLE_TYPE.custom

  const steps = hasInstallation ? STEPS : NO_INSTALLATION_STEPS

  const theme = useTheme()

  const [activeStep, setActiveStep] = useState(0)

  const handleStepPress = i => setActiveStep(prev => (prev < i ? prev : i))

  const goToNextStep = () => {
    setActiveStep(prev => (prev >= steps.length ? prev : ++prev))
    scrollToTop()
  }

  const scrollToTop = () => {
    scrollViewRef.current &&
      scrollViewRef.current.scrollTo({ y: 0, animated: false })
  }

  const showGradientHeader =
    (isCustomBundle && (!CBO.plan || !CBO.speed)) ||
    (activeStep === 0 && !isCustomBundle)

  const getStepperBody = index => {
    const step = steps[index].i18nKey
    switch (step) {
      case 'unlimited_offer_step':
        return (
          <OfferStep
            goNext={goToNextStep}
            scrollToTop={scrollToTop}
            getFixAndTVs={props.getFixAndTVs}
            selectBundle={props.selectBundle}
            getATLBundles={props.getATLBundles}
            getPegaBundles={props.getPegaBundles}
            getTariffPlans={props.getTariffPlans}
            getMemberSlots={props.getMemberSlots}
            getCoveredAreas={props.getCoveredAreas}
            validateLocation={props.validateLocation}
            changeBundleType={props.changeBundleType}
            resetCoverageFlag={props.resetCoverageFlag}
            updateCustomBundle={props.updateCustomBundle}
            resetLocationState={props.resetLocationState}
            resetCoverageError={props.resetCoverageError}
            getCustomBundlePrice={props.getCustomBundlePrice}
            checkCustomerByMSISDN={props.checkCustomerByMSISDN}
            resetCustomerCheckError={props.resetCustomerCheckError}
            updateCity={props.updateCity}
          />
        )
      case 'unlimited_id_step':
        return (
          <IdStep
            goNext={goToNextStep}
            validateName={props.validateName}
            validateEmail={props.validateEmail}
            changeUserData={props.changeUserData}
            createCustomer={props.createCustomer}
            checkCustomerByID={props.checkCustomerByID}
            validatePersonalNumber={props.validatePersonalNumber}
          />
        )
      case 'unlimited_group_step':
        return (
          <GroupStep
            goNext={goToNextStep}
            removeMember={props.removeMember}
            validateMember={props.validateMember}
            validateName={props.validateGroupName}
            changeGroupName={props.changeGroupName}
            formatPhoneNumber={props.formatPhoneNumber}
            validatePhoneNumber={props.validatePhoneNumber}
          />
        )
      case 'unlimited_installation_step':
        return (
          <InstallationStep
            goNext={goToNextStep}
            changeInstallData={props.changeInstallData}
          />
        )
      case 'unlimited_summary_step':
        return (
          <SummaryStep onCheckout={props.onCheckout} onClose={props.onClose} />
        )
      default:
        return null
    }
  }

  return (
    <View style={styles.flex}>
      <StatusBar
        barStyle={
          showGradientHeader || theme.isDark ? 'light-content' : 'dark-content'
        }
      />
      {/* Header */}
      {showGradientHeader ? (
        <HeaderWithGradient onClose={props.onClose} />
      ) : (
        <OfferDetailsHeader onClose={props.onClose} />
      )}
      {/* Timeline */}
      <View style={styles.timelineOuter(theme)}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[styles.scrollView, { width: '100%' }]}
        >
          <View style={styles.timelineContainer(theme)}>
            <HorizontalStepper
              steps={steps}
              style={styles.stepper}
              activeStepIndex={activeStep}
              onStepPress={handleStepPress}
            />
          </View>
        </ScrollView>
      </View>

      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.inner}>
          {/* Content */}
          <View style={styles.contentContainer(theme)}>
            <ScrollView
              ref={scrollViewRef}
              keyboardShouldPersistTaps={activeStep === 0 ? 'always' : 'never'}
              showsVerticalScrollIndicator={false}
              showsHorizontalScrollIndicator={false}
              style={styles.screenWrapper(activeStep, hasInstallation)}
            >
              {getStepperBody(activeStep)}
            </ScrollView>
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  )
}

UnlimitedHomeJourney.propTypes = {
  // General
  onClose: PropTypes.func,
  // Bundles
  selectBundle: PropTypes.func,
  getATLBundles: PropTypes.func,
  getPegaBundles: PropTypes.func,
  changeBundleType: PropTypes.func,
  // Custom Bundles
  getFixAndTVs: PropTypes.func,
  getTariffPlans: PropTypes.func,
  getMemberSlots: PropTypes.func,
  updateCustomBundle: PropTypes.func,
  getCustomBundlePrice: PropTypes.func,
  // Location
  getCoveredAreas: PropTypes.func,
  validateLocation: PropTypes.func,
  resetCoverageFlag: PropTypes.func,
  resetLocationState: PropTypes.func,
  resetCoverageError: PropTypes.func,
  resetCustomerCheckError: PropTypes.func,
  // Customer
  checkCustomerByMSISDN: PropTypes.func,
  // ID
  validateName: PropTypes.func,
  validateEmail: PropTypes.func,
  createCustomer: PropTypes.func,
  changeUserData: PropTypes.func,
  checkCustomerByID: PropTypes.func,
  validatePersonalNumber: PropTypes.func,
  // Member
  removeMember: PropTypes.func,
  validateMember: PropTypes.func,
  changeGroupName: PropTypes.func,
  formatPhoneNumber: PropTypes.func,
  validateGroupName: PropTypes.func,
  validatePhoneNumber: PropTypes.func,
  // Installation
  changeInstallData: PropTypes.func,
  // Summary
  onCheckout: PropTypes.func
}

const HeaderWithGradient = ({ onClose }) => {
  return (
    <LinearGradient
      angle={30}
      useAngle={true}
      angleCenter={{ x: 0, y: 0 }}
      colors={[Colors.darkRedTwo, Colors.vfRed]}
    >
      <VFImageHeader
        withSubHeader
        onClose={onClose}
        withOverlay={false}
        allowFontScaling={false}
        titleStyle={styles.white}
        headerStyle={styles.header}
        title="unlimited_my_offer"
        renderSubHeaderTitle={() => {
          return (
            <View style={[styles.days]}>
              <VFText
                i18nKey="unlimited_family_offer"
                style={[styles.white, styles.count]}
              />
            </View>
          )
        }}
        renderSubHeaderSubTitle={() => {
          return (
            <VFText
              i18nKey="unlimited_offer_description"
              style={[styles.white, styles.endDesc]}
            />
          )
        }}
        rightIcon={images.ic_close_white}
      />
    </LinearGradient>
  )
}

HeaderWithGradient.propTypes = {
  onClose: PropTypes.func
}

const OfferDetailsHeader = ({ onClose }) => {
  const {
    data: {
      language,
      bundleType,
      atlBundles,
      pegaBundles,
      customPlans,
      customTVs,
      customSlots,
      customSpeeds,
      selectedBundle,
      customBundlePrice,
      customBundleOptions: CBO
    }
  } = useContext(UnlimitedContext)

  const bundleData = getSelectedBundleData({
    customTVs,
    bundleType,
    atlBundles,
    pegaBundles,
    customPlans,
    customSlots,
    customSpeeds,
    selectedBundle,
    customBundlePrice,
    customBundleOptions: CBO
  })

  const validity = moment().add(10, 'd').format('DD/MM/YYYY')

  const isLoading =
    bundleType === BUNDLE_TYPE.custom &&
    (customPlans.isLoading ||
      customTVs.isLoading ||
      customSlots.isLoading ||
      customSpeeds.isLoading ||
      customBundlePrice.isLoading)

  const [showDetails, setShowDetails] = useState(false)

  const theme = useTheme()

  const { width, height } = useWindowDimensions()

  const isCustomBundle = bundleType === BUNDLE_TYPE.custom

  const USAGE_URI = `https://api-web-ci-vfal.vodafone.com/file/UnlimitedHome/faq.html?lang=${language}&dark=${theme.isDark}`

  return (
    <View style={styles.headerBg(theme)}>
      <VFImageHeader
        withSubHeader
        onClose={onClose}
        withOverlay={false}
        allowFontScaling={false}
        title="unlimited_my_offer"
        headerStyle={styles.header}
        renderSubHeaderTitle={() => null}
        subHeaderFullContainerStyle={styles.headerTitle}
        renderSubHeaderSubTitle={() => {
          return (
            <View>
              <VFTextWithLoader
                loaderHeight={24}
                loaderWidth={160}
                isLoading={isLoading}
                style={styles.mainTitle}
                i18nKey={isCustomBundle ? 'unlimited_family_offer' : '%1$s'}
                placeHolders={[bundleData?.name]}
              />
              <View style={styles.headerDescription(width)}>
                <View>
                  <VFTextWithLoader
                    loaderHeight={20}
                    loaderWidth={160}
                    style={styles.amount}
                    isLoading={isLoading}
                    i18nKey="unlimited_per_month"
                    placeHolders={[bundleData?.price?.value?.toString()]}
                  />
                </View>
                {bundleData?.discount?.value || isLoading ? (
                  <View style={styles.save}>
                    <Icon
                      size={24}
                      type="image"
                      style={styles.image}
                      name={UhImages.save}
                    />
                    <VFTextWithLoader
                      loaderWidth={80}
                      loaderHeight={20}
                      isLoading={isLoading}
                      style={styles.saveAmount}
                      i18nKey={'unlimited_save'}
                      placeHolders={[bundleData?.discount?.value]}
                    />
                  </View>
                ) : null}
              </View>
            </View>
          )
        }}
        renderDescriptionInfo={() => {
          return (
            <View style={styles.headerDescription(width)}>
              <VFText
                i18nKey="unlimited_valid_until"
                placeHolders={[validity]}
              />
              <TouchableOpacity
                activeOpacity={0.4}
                style={styles.viewBtnHolder}
                onPress={() => setShowDetails(true)}
              >
                <VFText
                  style={styles.viewMoreBtn}
                  i18nKey="unlimited_view_details"
                />
                <Icon
                  type="image"
                  name={UhImages.more}
                  style={styles.viewBtnIcon}
                />
              </TouchableOpacity>
            </View>
          )
        }}
      />
      {Platform.OS === 'web' && Dimensions.get('window').width > 700 ?
        <ReactNativeModal
          isVisible={showDetails}
          backdropOpacity={0.5}
          onSwipeComplete={() => setShowDetails(false)}
          onBackdropPress={() => setShowDetails(false)}
          propagateSwipe
          hideModalContentWhileAnimating
          hardwareAccelerated
          backdropTransitionOutTiming={0}
          style={{
            justifyContent: 'flex-end',
            margin: '0 !important',
            minWidth: 1920,
            maxWidth: 1920,
          }}
        >
          <View
            style={{
              minWidth: '600px',
              maxWidth: '600px',
              height: 'auto',
              backgroundColor: theme.colors.quickActionGradientColors.secondary,
              margin: 'auto',
              padding: '20px',
              paddingTop: null,
              borderRadius: "7px",
            }}
            onStartShouldSetResponder={() => true}>
            <View
              style={{ height: height * 0.75 }}
              onStartShouldSetResponder={() => true}
            >
              <WebView
                startInLoadingState={true}
                source={{ uri: USAGE_URI }}
                renderLoading={() => {
                  return (
                    <View style={{ height: height * 0.75 }}>
                      <Loading />
                    </View>
                  )
                }}
              />
              <View style={styles.topSpace}>
                <VFButton
                  title="unlimited_close"
                  onPress={() => setShowDetails(false)}
                />
              </View>
            </View>
          </View>
        </ReactNativeModal>
        : <QuickActionMenu
          type="secondary"
          isVisible={showDetails}
          titleStyle={styles.label}
          title="unlimited_usage_terms"
          headerStyle={styles.headerStyle}
          toggleModal={() => setShowDetails(false)}
        >
          <View
            style={{ height: height * 0.75 }}
            onStartShouldSetResponder={() => true}
          >
            <WebView
              startInLoadingState={true}
              source={{ uri: USAGE_URI }}
              renderLoading={() => {
                return (
                  <View style={{ height: height * 0.75 }}>
                    <Loading />
                  </View>
                )
              }}
            />
            <View style={styles.topSpace}>
              <VFButton
                title="unlimited_close"
                onPress={() => setShowDetails(false)}
              />
            </View>
          </View>
        </QuickActionMenu>}

    </View>
  )
}

OfferDetailsHeader.propTypes = {
  onClose: PropTypes.func
}

export default UnlimitedHomeJourney
